import { useContext, useState } from "react";
import { FormLogicOperation, FormOptionValidationOperator, QuestionType } from "../constants/common";
import FileService from "../services/FileService";
import Utils from "../utilities/Utils";
import { LoaderContext } from "../../App";
import { toast } from "react-toastify";
import FormService from "../services/FormService";


// Custom Hook for form shared functions and states
function useBaseForm() {

  const [formdata, setFormData] = useState('');
  const [responseData, setResponseData] = useState([]);
	const [histResponseData, setHistResponseData] = useState({});
  const [activeSectionData, setActiveSectionData] = useState({ id: 0 });
  const [isSubsection, setIsSubsection] = useState(false);
  const [parentSectionId, setParentSectionId] = useState(0);
  const [validationerrors, setValidationErrors] = useState([]);
  const { setLoaderState } = useContext(LoaderContext);
  const [previewFileData, setPreviewFileData] = useState(null);
  const [previewFileContent, setPreviewFileContent] = useState(null);
  const [previewFileType, setPreviewFileType] = useState(null);
  const [showFilePreviewModal, setShowFilePreviewModal] = useState(false);
  const [ocrData, setOcrData] = useState({
    modalDisplay: false,
    item: null,
    data: null,
    value: null,
    index: null
  });


	/**
	 * For formatting preview response >> The function is used to set 'hide_alt' property.
	 * This is to keep the original hide propert intact, when we perform updates on it.
	 * Note that all updates for hiding or unhiding w.r.t display logic is applied on 'hide_alt'.
	 * @param {*} data 
	 * @returns 
	 */

	const formatPreviewResponseForApplyingLogics = (data) => {
		const formattedData = data;
		formattedData.fields?.forEach(f => f.hide_alt = f.hide);
		formatSubSections(formattedData.sections);
		return formattedData;
	};

	const formatSubSections = (sections) => {
		let visibleSectionsCount = 0;
		sections?.forEach(s => {
			s.hide_alt = s.hide;
			let visibleFieldsCount = 0;
			s.fields?.forEach(f => {
				f.hide_alt = f.hide;
				if (!f.hide_alt) {
					visibleFieldsCount++;
				}
			});
			if (!s.hide) {
				// If section is not hidden as part of a logic 
				s.hide_alt = visibleFieldsCount == 0;
			}
			if (!s.hide_alt) {
				visibleSectionsCount++;
			}
			if (s.subSections?.length > 0) {
				visibleSectionsCount += formatSubSections(s.subSections, s);
				if (!s.hide) {
					s.hide_alt = visibleSectionsCount == 0;
				}
			}
		});
		return visibleSectionsCount;
	};

	const initializeResponseForField = (field, response) => {
		if (field.type === QuestionType.MULTI_SELECT_DROP_DOWN
			|| field.type === QuestionType.MULTIPLE_CHOICE
			|| field.type === QuestionType.DROP_DOWN
			|| field.type === QuestionType.RADIO) {
			response['responseOptions'] = [];
		} else if (field.type === QuestionType.SINGLE_TEXTBOX
			|| field.type === QuestionType.TEXT_BOX) {
			response['textResponse'] = '';
		} else if (field.type === QuestionType.DATE_TIME_BOX) {
			response['dateResponse'] = '';
		} else if (field.type === QuestionType.DATE_RANGE) {
			response['dateResponse'] = '';
			response['toDateResponse'] = '';
		} else if (field.type === QuestionType.FILE_UPLOAD) {
			response['files'] = [];
		} else if (field.type === QuestionType.NUMERIC_VALUE) {
			response['numericResponse'] = '';
			response['uom'] = '';
			if (field.defaultUOM) {
				response['uom'] = field.defaultUOM;
			}
		}
	};

  const multiSelectArraySet = (formField, responseOptions) => {
    let filterArray = [];
    let arrayId = [];
    if (formField) {
      let iterate = responseOptions;

      for (let k = 0; k < iterate.length; k++) {
        let obj = {
          id: iterate[k].id
        }
        arrayId.push(obj);
        let filteredOption = formField.options.filter(x => x.id === iterate[k].id);
        if (filteredOption[0]) {
          filterArray.push(filteredOption[0]);
        }
      }
    }
    let returnObj = {
      arrayId: arrayId,
      filterArray: filterArray
    }
    return returnObj;
  };

  const calculateTotalQuestionCount = (sections, countData) => {
    sections?.forEach(s => {
      if (s.hide_alt) return;

      s.fields?.forEach(f => {
        if (f.hide_alt) return;

        countData.count++;
      });
      if (s.subSections?.length > 0) {
        calculateTotalQuestionCount(s.subSections, countData);
      }
    });
  };

	/**
   * This method applies logic rules based on the repsonse given to a field.
   * @param {*} param0 
   * @returns 
   */
  const applyLogicDefinitonsOnFieldResponse = ({ field, index, responseArray, formSkeleton, histResponseArray, forPreview = false }) => {
    let fieldResponses;
    if (field.type === QuestionType.RADIO || field.type === QuestionType.DROP_DOWN
      || field.type === QuestionType.MULTI_SELECT_DROP_DOWN || field.type === QuestionType.MULTIPLE_CHOICE) {
      fieldResponses = responseArray[index]['responseOptions'];
    } else {
      return;
    }
    field.logicDefinitions?.forEach(logic => {
      let isRuleApplicable = false;
      logic.rules?.forEach(rule => {
        fieldResponses?.forEach(fr => {
          const isIntegerBasedRule = rule.operation === FormLogicOperation.GREATER_THAN
            || rule.operation === FormLogicOperation.GREATER_THAN_EQUAL
            || rule.operation === FormLogicOperation.LESS_THAN
            || rule.operation === FormLogicOperation.LESS_THAN_EQUAL;
          const lhs = isIntegerBasedRule ? +fr.name ?? 0 : fr.id;
          const rhs = isIntegerBasedRule
            ? +(field.options?.find(o => o.id === rule.option.id)?.name) ?? 0
            : rule.option.id;
          isRuleApplicable = isRuleApplicable || operateRule(lhs, rhs, rule.operation);
        });
      });
      logic.displayFields?.forEach(displayField => {
        if (displayField.fields?.filter(f => f.id > 0)?.length > 0) {
          displayField.fields.forEach(f => {
            setFieldsOrSectionsVisibility({
              id: f.id,
              isRuleApplicable: isRuleApplicable,
              isField: true,
              formSkeleton: formSkeleton,
              responseArray: responseArray,
              histResponseArray: histResponseArray,
              forPreview: forPreview
            });
          });
        } else {
          const fieldId = displayField.subSectionId > 0
            ? displayField.subSectionId
            : displayField.sectionId;
          setFieldsOrSectionsVisibility({
            id: fieldId,
            isRuleApplicable: isRuleApplicable,
            isField: false,
            formSkeleton: formSkeleton,
            responseArray: responseArray,
            histResponseArray: histResponseArray,
            forPreview: forPreview
          });
        }
      });
    });
  };  

  const setFieldsOrSectionsVisibility = ({ id, isRuleApplicable, isField, formSkeleton, responseArray, histResponseArray, forPreview = false }) => {
    if (!id) return;

    if (isField) {
      findAndToggleFieldDisplayInForm(id, isRuleApplicable ? false : true, formSkeleton, responseArray, histResponseArray, forPreview);
    } else {
      findAndToggleSectionDisplayInForm(id, isRuleApplicable ? false : true, formSkeleton, responseArray, histResponseArray, forPreview);
    }
    if (forPreview || activeSectionData) return;

    // Set active section also if unavailable
    if (formSkeleton.fields?.length > 0) {
      setActiveSectionData({
        fields: formSkeleton.fields
      });
    } else if (formSkeleton.sections.length > 0) {
      const firstSection = formSkeleton.sections[0];
      if (firstSection.fields.length > 0) {
        setActiveSectionData(firstSection);
      } else if (firstSection.subSections.length > 0) {
        setIsSubsection(true);
        setParentSectionId(firstSection.id);
        setActiveSectionData(firstSection.subSections[0]);
      }
    }
  };

  const operateRule = (lhs, rhs, operation) => {
    switch (operation) {
      case FormLogicOperation.AND: return lhs && rhs;
      case FormLogicOperation.OR: return lhs || rhs;
      case FormLogicOperation.EQUAL: return lhs === rhs;
      case FormLogicOperation.NOT_EQUAL: return lhs !== rhs;
      case FormLogicOperation.GREATER_THAN: return lhs > rhs;
      case FormLogicOperation.GREATER_THAN_EQUAL: return lhs >= rhs;
      case FormLogicOperation.LESS_THAN: return lhs < rhs;
      case FormLogicOperation.LESS_THAN_EQUAL: return lhs <= rhs;
      default: return false;
    }
  };

	const findAndToggleFieldDisplayInForm = (fieldId, hide, formSkeleton, responseArray, histResponseArray, forPreview) => {
    let data = formSkeleton;
    setFormData('');
    const rootFields = toggleFieldDisplayInFields(fieldId, data.fields, hide);
    data.fields = rootFields.fields;
    if (rootFields.found) {
      if (hide) {
        // If resetting to the original state
        const hiddenField = findField(fieldId, data);
        if (hiddenField) {
          if (!forPreview) {
            resetResponseForHiddenField(hiddenField, responseArray, histResponseArray);
          }
          resetLogicallyRelatedSectionsAndResponsesForHiddenField(hiddenField, data, responseArray, histResponseArray, forPreview);
          setResponseData(responseArray);
          setHistResponseData(histResponseArray);
        }
      }
    } else {
      data.sections = toggleFieldDisplayInSections(fieldId, data.sections, hide);
      if (hide) {
        const hiddenField = findField(fieldId, data);
        if (hiddenField) {
          if (!forPreview) {
            resetResponseForHiddenField(hiddenField, responseArray,histResponseArray);
          }
          resetLogicallyRelatedSectionsAndResponsesForHiddenField(hiddenField, data, responseArray, histResponseArray, forPreview);
          setResponseData(responseArray);
          setHistResponseData(histResponseArray);
        }
      }
    }
    resetSectionVisibility(data.sections);
    setFormData(data);
  };

  const findAndToggleSectionDisplayInForm = (sectionId, hide, formSkeleton, responseArray, histResponseArray, forPreview) => {
    let data = formSkeleton;
    setFormData('');
    data.sections = toggleSectionDisplayInSections(sectionId, data.sections, hide);
    if (hide) {
      // Reset everything switched ON as part of the logic
      // Start from the section that was hidden just now and then drill into it's logic
      // recursively and hide every section related to that.
      const hiddenSection = findSectionInForm(sectionId, data);
      if (hiddenSection) {
        resetLogicallyRelatedSectionsAndResponsesForHiddenSection(hiddenSection, data, responseArray, histResponseArray, forPreview);
        setResponseData(responseArray);
        setHistResponseData(histResponseArray);
      }
    }
    resetSectionVisibility(data.sections);
    setFormData(data);
  };

	/**
   * Finds a field by recursively traversing the formdata tree.
   * Note that fields can be there in sections and subsections.
   * @param {*} id 
   * @returns 
   */

  const findField = (id, form = null) => {
    const formDataToIterate = form ?? formdata;
    const field = formDataToIterate?.fields?.find(f => f.id === id);
    if (field) {
      return field;
    } else {
      return findFieldRecursive(formDataToIterate.sections, id);
    }
  };

  const findFieldRecursive = (sections, id) => {
    let field;
    for (let index = 0; index < sections?.length; index++) {
      const section = sections[index];
      field = section.fields?.find(f => f.id === id);
      if (field) {
        break;
      } else {
        field = findFieldRecursive(section.subSections, id);
        if (field) return field;
      }
    }
    return field;
  };

  /**
   * Finds a section with the given id by recursively traversing the formdata tree
   * @param {*} id 
   * @returns 
   */

  const findSection = (id) => {
    return findSectionRecursive(formdata.sections, id);
  };

  const findSectionRecursive = (sections, id) => {
    let section;
    for (let index = 0; index < sections?.length; index++) {
      const sec = sections[index];
      if (sec.id === id) {
        section = sec;
        break;
      } else {
        section = findSectionRecursive(sec.subSections, id);
        if (section) return section;
      }
    }
    return section;
  };

  const findSectionAndReset = (sections, id) => {
    let section;
    for (let index = 0; index < sections?.length; index++) {
      const sec = sections[index];
      if (sec.id === id) {
        sec.hide_alt = sec.hide;
        section = sec;
        resetVisibilityOfChildren(sec);
        break;
      } else {
        section = findSectionAndReset(sec.subSections, id);
        if (section) return section;
      }
    }
    return section;
  };

  const toggleFieldDisplayInFields = (id, fields, hide) => {
    let found = false;
    for (let index = 0; index < fields?.length; index++) {
      const f = fields[index];
      if (f.id === id) {
        f.hide_alt = hide;
        found = true;
        break;
      }
    }
    return { fields: fields, found: found };
  };

  const toggleFieldDisplayInSections = (id, sections, hide) => {
    sections?.forEach(sec => {
      let found = false;
      for (let index = 0; index < sec?.fields?.length; index++) {
        const f = sec.fields[index];
        if (f.id === id) {
          f.hide_alt = hide;
          found = true;
          break;
        }
      }
      if (!found && sec.subSections?.length > 0) {
        sec.subSections = toggleFieldDisplayInSections(id, sec.subSections, hide);
      }
    });
    return sections;
  };

  const toggleSectionDisplayInSections = (id, sections, hide) => {
    for (let index = 0; index < sections?.length; index++) {
      const sec = sections[index];
      if (sec.id === id) {
        sec.hide_alt = hide;
        if (hide) {
          resetVisibilityOfChildren(sec);
        }
        break;
      } else {
        sec.subSections = toggleSectionDisplayInSections(id, sec.subSections, hide);
      }
    }
    return sections;
  };

	/**
   * For a given section, this method finds the logics associated with its fields and recursively,
   * finds them for its corresponding subsections. It then blindly, resets the response for the field
   * that has a logic defined. Iterates through each logic and hides the section or subsection involved 
   * in the logic. It does the same all over again for the corresponding section or subsection as well.
   * 
   * Example, if the form has 3 sections A, B and C. B is enabled on answering question A1 and C is enabled on
   * answering question B1. Lets assume the user has asnwered A1 and B1. Now he can view all 3 sections.
   * Now if he negates his answer in A1, then B and C both should be hidden, not just B.
   * @param {*} section 
   * @param {*} formSkeleton 
   * @param {*} responses 
   */
  const resetLogicallyRelatedSectionsAndResponsesForHiddenSection = (section, formSkeleton, responses, histResponses, forPreview) => {
    section?.fields?.forEach(f => {
      if (f.logicDefinitions?.length === 0) return;

      const responseIndex = responses.findIndex(r => r.formField?.id === f.id);
      if (responseIndex >= 0) {
        responses[responseIndex]['responseOptions'] = [];
      }
      f.logicDefinitions?.forEach(logic => {
        logic.displayFields?.forEach(displayField => {
          if (displayField.fields?.length > 0) {
            displayField.fields.forEach(f => {
              const fieldAlt = findField(f.id, formSkeleton);
              if (!fieldAlt) return;

              fieldAlt.hide_alt = fieldAlt.hide;
              if (!forPreview) {
                resetResponseForHiddenField(fieldAlt, responses, histResponses);
              }
              resetLogicallyRelatedSectionsAndResponsesForHiddenField(fieldAlt, formSkeleton, responses, histResponses, forPreview);
            });
          } else if (displayField.subSectionId > 0) {
            const sec = findSectionAndReset(formSkeleton.sections, displayField.subSectionId);
            resetLogicallyRelatedSectionsAndResponsesForHiddenSection(sec, formSkeleton, responses, histResponses, forPreview);
          } else if (displayField.sectionId > 0) {
            const sec = findSectionAndReset(formSkeleton.sections, displayField.sectionId);
            resetLogicallyRelatedSectionsAndResponsesForHiddenSection(sec, formSkeleton, responses, histResponses, forPreview);
          }
        });
      });
    });
    section.subSections?.forEach(subSection => {
      resetLogicallyRelatedSectionsAndResponsesForHiddenSection(subSection, formSkeleton, responses, forPreview);
    });
  };


  /**
   * This method is similar to resetLogicallyRelatedSectionsAndResponsesForHiddenSection.
   * Only difference being, it runs for a given field which is hidden.
   * @param {*} field 
   * @param {*} formSkeleton 
   * @param {*} responses 
   * @returns 
   */
  const resetLogicallyRelatedSectionsAndResponsesForHiddenField = (field, formSkeleton, responses, histResponses, forPreview) => {
    if (field.logicDefinitions?.length === 0) return;

    const responseIndex = responses.findIndex(r => r.formField?.id === field.id);
    if (responseIndex >= 0) {
      responses[responseIndex]['responseOptions'] = [];
    }
    field.logicDefinitions?.forEach(logic => {
      logic.displayFields?.forEach(displayField => {
        if (displayField.fields?.length > 0) {
          displayField.fields.forEach(f => {
            const fieldAlt = findField(f.id, formSkeleton);
            if (!fieldAlt) return;

            fieldAlt.hide_alt = fieldAlt.hide;
            if (!forPreview) {
              resetResponseForHiddenField(fieldAlt, responses, histResponses);
            }
            resetLogicallyRelatedSectionsAndResponsesForHiddenField(fieldAlt, formSkeleton, responses, histResponses, forPreview);
          });
        } else if (displayField.subSectionId > 0) {
          const sec = findSectionAndReset(formSkeleton.sections, displayField.subSectionId);
          resetLogicallyRelatedSectionsAndResponsesForHiddenSection(sec, formSkeleton, responses, histResponses, forPreview);
        } else if (displayField.sectionId > 0) {
          const sec = findSectionAndReset(formSkeleton.sections, displayField.sectionId);
          resetLogicallyRelatedSectionsAndResponsesForHiddenSection(sec, formSkeleton, responses, histResponses, forPreview);
        }
      });
    });
  };

  const resetVisibilityOfChildren = (section) => {
    section.fields?.forEach(f => f.hide_alt = f.hide);
    section?.subSections?.forEach(s => {
      s.hide_alt = s.hide;
      resetVisibilityOfChildren(s);
    });
  };

  const resetSectionVisibility = (sections) => {
    let visibleSectionsCount = 0;
    sections?.forEach(s => {
      let visibleFieldsCount = 0;
      s.fields?.forEach(f => {
        if (!f.hide_alt) {
          visibleFieldsCount++;
        }
      });
      if (!s.hide) {
        s.hide_alt = visibleFieldsCount == 0;
      }
      if (!s.hide_alt) {
        visibleSectionsCount++;
      }
      if (s.subSections?.length > 0) {
        const visibleSubSectionsCount = resetSectionVisibility(s.subSections);
        if (s.hide_alt) {
          // If the section is already hidden then check if it has visibleSubsections
          // It the section is visible already, means that it has some visible fields, then do not do anything
          s.hide_alt = visibleSubSectionsCount == 0;
        }
      }
    });
    return visibleSectionsCount;
  };

  const getInitializedHistResponseArrayFor = (fields, formSkeleton) => {
    const histResData = JSON.parse(JSON.stringify(histResponseData));
    if (Object.keys(histResData)?.length === 0) {
      formSkeleton?.historicalDataYears?.forEach(year => {
        histResData[year] = { submissionId: null, responses: [] }; 
      });
    }
    Object.keys(histResData)?.forEach(year => {
      fields?.forEach(field => {
        if (!field.historicalDataSupported) return;

        const obj = {
          formField: {
            id: field.id
          }
        };
        if (field.type === QuestionType.NUMERIC_VALUE) {
          obj['numericResponse'] = '';
          obj['uom'] = '';
          if (field.defaultUOM) {
            obj['uom'] = field.defaultUOM;
          }
        }
        if (histResData[year].responses?.find(r => r.formField?.id === field.id)) return;
  
        histResData[year].responses?.push(obj);
      });
    });    
    return histResData;
  };

  const populateHistoricResponses = (linkedSubmissions, histResData) => {
    if (!linkedSubmissions || linkedSubmissions?.length === 0) return;

    Object.keys(histResData)?.forEach(year => {
      const linkedSubmission = linkedSubmissions?.find(s => s.period?.split('-')[0] === `${year}`);
      if (!linkedSubmission) return;

      histResData[year].submissionId = linkedSubmission.submissionId;
      histResData[year]?.responses?.forEach(histResponse => {
        const matchingSubmittedRes = linkedSubmission?.responses?.find(r => r.formField.id === histResponse.formField.id);
        if (!matchingSubmittedRes) return;

        if (matchingSubmittedRes.fieldType === QuestionType.NUMERIC_VALUE) {
          histResponse['numericResponse'] = matchingSubmittedRes.numericResponse ?? '';
          if (matchingSubmittedRes.uom) {
            histResponse['uom'] = matchingSubmittedRes.uom;
          }
        }
      });
    });
  };

  const resetResponseForHiddenField = (field, responseArray, histResArray) => {
    if (!field.hide_alt) return;

    const responseIndex = responseArray.findIndex(r => r.formField?.id === field.id);
    if (responseIndex >= 0) {
      initializeResponseForField(field, responseArray[responseIndex]);
    }
    if (field.historicalDataSupported && field.type === QuestionType.NUMERIC_VALUE) {
      Object.keys(histResArray)?.forEach(k => {
        const resIndex = histResArray[k]?.responses?.findIndex(r => r.formField?.id === field.id);
        histResArray[k].responses[resIndex]['numericResponse'] = '';
        histResArray[k].responses[resIndex]['uom'] = '';
        if (field.defaultUOM) {
          histResArray[k].responses[resIndex]['uom'] = field.defaultUOM;
        }
      });      
		}
  };

  const initializeFormQuestions = (initArray, sections) => {
    sections?.forEach((section) => {
      section?.fields?.forEach((field) => {
        const obj = {
          formField: {
            id: field.id,
            sectionId: section.id
          }
        }
        initializeResponseForField(field, obj);
        initArray.push(obj);
      })
      initializeFormQuestions(initArray, section.subSections);
    })
  };

  const initializeHistResponseArrayFor = (fields, formSkeleton, histResData, sectionId = 0) => {
    if (Object.keys(histResData)?.length === 0) {
      formSkeleton?.historicalDataYears?.forEach(year => {
        histResData[year] = { submissionId: null, responses: [] };
      });
    }
    Object.keys(histResData)?.forEach(year => {
      fields?.forEach(field => {
        if (!field.historicalDataSupported) return;

        const obj = {
          formField: {
            id: field.id,
            sectionId: sectionId
          }
        };
        if (field.type === QuestionType.NUMERIC_VALUE) {
          obj['numericResponse'] = '';
          obj['uom'] = '';
          if (field.defaultUOM) {
            obj['uom'] = field.defaultUOM;
          }
        }
        if (histResData[year].responses?.find(r => r.formField?.id === field.id)) return;

        histResData[year].responses?.push(obj);
      });
    });
  };

  const initializeFormQuestionsForHistResponse = (initArray, sections, form) => {
    sections?.forEach((section) => {
      initializeHistResponseArrayFor(section?.fields, form, initArray, section.id);
      initializeFormQuestionsForHistResponse(initArray, section.subSections, form);
    })
  };

  const findSectionInForm = (id, formSkeleton) => {
    return findSectionRecursive(formSkeleton.sections, id);
  };

  const isCommentInvalid = (id, isMandatory, remarkResponse) => {
    return checkValidationError(id) && isMandatory && !remarkResponse;
  };

  const isFileInvalid = (id, isMandatory, uploadedFiles) => {
    return checkValidationError(id) && isMandatory && !(uploadedFiles.length > 0);
  };

  const checkValidationError = (fId) => {
    if (fId && validationerrors?.length > 0) {
      if (validationerrors.includes(fId)) {
        return true;
      }
    }
  };

  const handleFileDownload = ({ id, token, submissionId, isFormInReview }) => {
    FileService.downloadFile({ 
      token: token, 
      fileId: id, 
      submissionId: submissionId,
      isReview: isFormInReview
    })
      .then((response) => {
        let fetchDataModified = `data:${response.data.contentType};base64,${response.data.content}`;
        let link = document.createElement("a");
        link.href = fetchDataModified;
        link.download = response.data.fileName;
        link.click();
      })
      .catch((error) => { });
  };

  const handleFilePreview = async ({fileId, fileName, token, submissionId, isFormInReview  }) => {
    // If it's an office document, trigger download instead of preview
    if (Utils.isOfficeDocument(fileName)) {
      handleFileDownload({ 
        id: fileId, 
        token: token,
        submissionId: submissionId,
        isFormInReview: isFormInReview
      });
      return;
    }
    
    setLoaderState({ display: true });
    
    try {
      const response = await FileService.downloadFile({ 
        token: token, 
        fileId: fileId, 
        submissionId: submissionId,
        isReview: isFormInReview
      });
      
      const contentType = response.data.contentType;
      const content = response.data.content;
      const fileName = response.data.fileName;
      
      setPreviewFileData({
        id: fileId,
        fileName: fileName,
        contentType: contentType,
        content: content
      });
      
      // Determine file type and set preview content
      const fileExtension = fileName.split('.').pop()?.toLowerCase();
      setPreviewFileType(fileExtension);
      
      if (contentType.startsWith('image/')) {
        // For images, create a data URL
        const dataUrl = `data:${contentType};base64,${content}`;
        setPreviewFileContent(dataUrl);
      } else if (contentType === 'application/pdf') {
        // For PDFs, create a data URL
        const dataUrl = `data:${contentType};base64,${content}`;
        setPreviewFileContent(dataUrl);
      } else if (contentType.includes('text/') || 
                 contentType === 'application/json' || 
                 contentType === 'application/xml' ||
                 fileExtension === 'csv') {
        // For text files, decode the content
        const textContent = atob(content);
        setPreviewFileContent(textContent);
      } else {
        // For other file types, show a download option
        setPreviewFileContent(null);
      }
      
      setShowFilePreviewModal(true);
    } catch (error) {
      toast.error('Failed to load file preview', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoaderState({ display: false });
    }
  };
  
  const handleFilePreviewClose = () => {
    setShowFilePreviewModal(false);
    setPreviewFileData(null);
    setPreviewFileContent(null);
    setPreviewFileType(null);
  };

  const removeFile = (index) => {
    let arr = [...responseData];
    arr[index].files = [];
    setResponseData(arr);
  };

  const validateOptionSelection = (item, selectedCount) => {
    if (!item.optionSelectionValidation || item.type !== QuestionType.MULTIPLE_CHOICE) {
      return true;
    }

    const validation = item.optionSelectionValidation;
    const { operator, count, maximumCount } = validation;

    if (!operator || count === null) {
      return true; 
    }

    switch (operator) {
      case FormOptionValidationOperator.EQUAL_TO:
        return selectedCount === count;
      case FormOptionValidationOperator.AT_LEAST:
        return selectedCount >= count;
      case FormOptionValidationOperator.AT_MOST:
        return selectedCount <= count;
      case FormOptionValidationOperator.BETWEEN:
        return selectedCount >= count && selectedCount <= (maximumCount || count);
      default:
        return true;
    }
  };

  const updateOptionSelectionValidation = (item, selectedCount) => {
    if (item.type !== QuestionType.MULTIPLE_CHOICE || !item.optionSelectionValidation) {
      return;
    }

    const isValid = validateOptionSelection(item, selectedCount);
    const currentErrors = [...validationerrors];

    if (!isValid) {
      if (!currentErrors.includes(item.id)) {
        currentErrors.push(item.id);
        setValidationErrors(currentErrors);
      }
    } else {
      const errorIndex = currentErrors.indexOf(item.id);
      if (errorIndex > -1) {
        currentErrors.splice(errorIndex, 1);
        setValidationErrors(currentErrors);
      }
    }
  };

  const getOptionValidationMessage = (validation) => {
    if (!validation || !validation.operator || validation.count === null) {
      return '';
    }

    const { operator, count, maximumCount } = validation;
    
    switch (operator) {
      case FormOptionValidationOperator.EQUAL_TO:
        return `exactly ${count} option${count !== 1 ? 's' : ''}`;
      case FormOptionValidationOperator.AT_LEAST:
        return `at least ${count} option${count !== 1 ? 's' : ''}`;
      case FormOptionValidationOperator.AT_MOST:
        return `at most ${count} option${count !== 1 ? 's' : ''}`;
      case FormOptionValidationOperator.BETWEEN:
        return `between ${count} and ${maximumCount || count} options`;
      default:
        return '';
    }
  };

  const shouldDisableOption = (item, selectedCount, isChecked) => {
    if (!item.optionSelectionValidation || item.type !== QuestionType.MULTIPLE_CHOICE || isChecked) {
      return false;
    }

    const { operator, count, maximumCount } = item.optionSelectionValidation;

    if (!operator || count === null) {
      return false;
    }

    switch (operator) {
      case FormOptionValidationOperator.EQUAL_TO:
        return selectedCount === count;
      case FormOptionValidationOperator.AT_MOST:
        return selectedCount >= count;
      case FormOptionValidationOperator.BETWEEN:
        return maximumCount !== null && selectedCount >= maximumCount;
      default:
        return false;
    }
  };

	return {
		// Functions
		formatPreviewResponseForApplyingLogics,
		initializeResponseForField,
		multiSelectArraySet,
		calculateTotalQuestionCount,
		applyLogicDefinitonsOnFieldResponse,
    getInitializedHistResponseArrayFor,
    populateHistoricResponses,
    findField,
    initializeFormQuestions,
    initializeHistResponseArrayFor,
    initializeFormQuestionsForHistResponse,
    findSectionInForm,
    isCommentInvalid,
    isFileInvalid,
    checkValidationError,
    validateOptionSelection,
    updateOptionSelectionValidation,
    getOptionValidationMessage,
    shouldDisableOption,
    handleFileDownload,
    handleFilePreview,
    handleFilePreviewClose,
    removeFile,
		// States
		formdata, setFormData,
		responseData, setResponseData,
		histResponseData,setHistResponseData,
    activeSectionData, setActiveSectionData,
    isSubsection, setIsSubsection,
    parentSectionId, setParentSectionId,
    validationerrors, setValidationErrors,
    setLoaderState,
    previewFileData, setPreviewFileData,
    previewFileContent, setPreviewFileContent,
    previewFileType, setPreviewFileType,
    showFilePreviewModal, setShowFilePreviewModal,
    ocrData, setOcrData
	};
}

export default useBaseForm;
